import type { FC, ReactNode, RefObject } from 'react'
import type { TileCell } from '@/components/landing/tiles'
import type { TriggerState } from '@/hooks/use-in-view-trigger'

import { useEffect, useState } from 'react'
import { Check, Hand } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { CountUp } from '@/components/generals/count-up'
import { LogoMark } from '@/components/generals/logo'
import { SectionBadge } from '@/components/landing/section-badge'
import { GlassTiles, TileField } from '@/components/landing/tiles'
import { useInViewTrigger } from '@/hooks/use-in-view-trigger'

const CORNER_TILES: TileCell[] = [[0, 0], [2, 0], [1, 1], [3, 1], [0, 2]]

const RUN_LOG = [
    { time: '09:00:02', text: 'Pulled 128 payouts from Stripe', escalated: false },
    { time: '09:00:41', text: 'Matched 124 to open invoices', escalated: false },
    { time: '09:01:03', text: 'Sent 4 mismatches to Priya', escalated: true },
    { time: '09:01:05', text: 'Posted the summary in #finance', escalated: false }
]

const WEEK = [
    { day: 'Mon', value: 1840 },
    { day: 'Tue', value: 2110 },
    { day: 'Wed', value: 1985 },
    { day: 'Thu', value: 2260 },
    { day: 'Fri', value: 2195 },
    { day: 'Sat', value: 940 },
    { day: 'Sun', value: 880 }
]

const WEEK_MAX = Math.max(...WEEK.map((entry) => entry.value))

const PROMPT = "Every morning, match yesterday's Stripe payouts to open invoices and flag anything that doesn't add up."
const TYPE_SPEED = 26
const TOOLS = ['Stripe', 'QuickBooks', 'Slack']

// Rows wait hidden below the fold, then rise in one after another.
const RUN_STAGGER = 'group-data-[anim=armed]/run:opacity-0 motion-safe:group-data-[anim=play]/run:animate-rise'

interface PanelProps {
    children: ReactNode
    panelRef: RefObject<HTMLDivElement | null>
    state: TriggerState
    group: string
}

const Panel: FC<PanelProps> = ({ children, panelRef, state, group }): ReactNode => {
    return (
        <div
            ref={panelRef}
            data-anim={state}
            className={cn('w-full rounded-[22px] border border-white/80 bg-white/85 p-4 shadow-[0_22px_44px_-26px_rgba(46,52,98,0.45)] backdrop-blur-md', group)}
        >
            {children}
        </div>
    )
}

// Types the brief out once it scrolls into view, then the connected tools pop in.
const BuildVisual: FC = (): ReactNode => {
    const [panelRef, state] = useInViewTrigger<HTMLDivElement>()
    const [typed, setTyped] = useState<number | null>(null)

    useEffect(() => {
        if (state === 'armed') {
            setTyped(0)

            return
        }

        if (state !== 'play') return

        let count = 0
        let timer = 0

        const start = window.setTimeout(() => {
            timer = window.setInterval(() => {
                count += 1
                setTyped(count)

                if (count >= PROMPT.length) window.clearInterval(timer)
            }, TYPE_SPEED)
        }, 450)

        return () => {
            window.clearTimeout(start)
            window.clearInterval(timer)
        }
    }, [state])

    const done = typed === null || typed >= PROMPT.length
    const text = typed === null ? PROMPT : PROMPT.slice(0, typed)

    return (
        <Panel panelRef={panelRef} state={state} group='group/build'>
            <div className='flex items-center justify-between'>
                <span className='flex items-center gap-2 text-[12.5px] font-semibold text-ink'>
                    <span className='flex size-6 items-center justify-center rounded-full bg-meadow'>
                        <LogoMark className='h-[9px]' />
                    </span>
                    New agent
                </span>

                <span
                    className={cn(
                        'rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors duration-500',
                        done ? 'bg-meadow text-ink' : 'bg-sage text-ink/80'
                    )}
                >
                    {done ? 'Ready to run' : 'Drafting'}
                </span>
            </div>

            <p className='relative mt-3 rounded-2xl bg-mist px-3.5 py-3 text-left text-[13px] leading-relaxed text-ink/85'>
                <span className='invisible'>{PROMPT}</span>

                <span className='absolute inset-0 px-3.5 py-3'>
                    {text}
                    <span className='ml-0.5 inline-block h-3.5 w-px translate-y-0.5 bg-ink motion-safe:animate-caret' />
                </span>
            </p>

            <div className='mt-3 flex flex-wrap gap-1.5'>
                {TOOLS.map((tool, index) => (
                    <span
                        key={tool}
                        className={cn(
                            'rounded-full border border-border bg-white px-2.5 py-1 text-[11.5px] font-medium text-ink/80',
                            !done && 'opacity-0',
                            done && state === 'play' && 'motion-safe:animate-pop'
                        )}
                        style={done && state === 'play' ? { animationDelay: `${150 + index * 120}ms` } : undefined}
                    >
                        {tool}
                    </span>
                ))}
            </div>
        </Panel>
    )
}

// Each step of the run lands in turn, the way it would in the live log.
const RunVisual: FC = (): ReactNode => {
    const [panelRef, state] = useInViewTrigger<HTMLDivElement>()

    return (
        <Panel panelRef={panelRef} state={state} group='group/run'>
            <div className='flex items-center justify-between'>
                <span className='text-[12.5px] font-semibold text-ink'>
                    Invoice Reconciler
                </span>

                <span className='flex items-center gap-1.5 text-[11.5px] font-medium text-moss'>
                    <span className='size-1.5 rounded-full bg-moss motion-safe:animate-ping-soft' />
                    Running
                </span>
            </div>

            <ol className='mt-3.5 space-y-2.5 text-left'>
                {RUN_LOG.map((entry, index) => (
                    <li
                        key={entry.time}
                        className={cn('flex items-center gap-2.5 text-[12.5px]', RUN_STAGGER)}
                        style={{ animationDelay: `${500 + index * 520}ms` }}
                    >
                        <span
                            className={cn(
                                'flex size-5 shrink-0 items-center justify-center rounded-full',
                                entry.escalated ? 'bg-[#f8dcc9] text-[#8a3f14]' : 'bg-meadow/70 text-ink'
                            )}
                        >
                            {entry.escalated ? <Hand className='size-3' /> : <Check className='size-3' strokeWidth={3} />}
                        </span>

                        <span className='min-w-0 flex-1 truncate text-ink/85'>
                            {entry.text}
                        </span>

                        <span className='font-mono text-[10.5px] text-muted-foreground tabular-nums'>
                            {entry.time}
                        </span>
                    </li>
                ))}
            </ol>
        </Panel>
    )
}

// The week's bars grow from the baseline and the headline figures count up.
const MonitorVisual: FC = (): ReactNode => {
    const [panelRef, state] = useInViewTrigger<HTMLDivElement>()

    return (
        <Panel panelRef={panelRef} state={state} group='group/monitor'>
            <div className='flex items-baseline justify-between'>
                <span className='text-[12.5px] font-semibold text-ink'>
                    This week
                </span>

                <span className='text-[11px] text-muted-foreground'>
                    All agents
                </span>
            </div>

            <div className='mt-3 grid grid-cols-2 gap-2 text-left'>
                <div className='rounded-2xl bg-mist px-3 py-2.5'>
                    <p className='text-[11px] text-muted-foreground'>Success rate</p>
                    <p className='mt-0.5 font-heading text-[22px] font-semibold tracking-[-0.03em] text-ink'>
                        <CountUp value='99.2%' duration={1600} />
                    </p>
                </div>

                <div className='rounded-2xl bg-mist px-3 py-2.5'>
                    <p className='text-[11px] text-muted-foreground'>Cost per task</p>
                    <p className='mt-0.5 font-heading text-[22px] font-semibold tracking-[-0.03em] text-ink'>
                        <CountUp value='$0.03' duration={1600} />
                    </p>
                </div>
            </div>

            <p className='mt-3 text-left text-[11px] text-muted-foreground'>
                Tasks completed per day
            </p>

            <div className='mt-2 flex h-14 items-end gap-[2px] border-b border-border'>
                {WEEK.map((entry, index) => (
                    <div
                        key={entry.day}
                        title={`${entry.day}: ${entry.value.toLocaleString('en-US')} tasks`}
                        className='flex h-full flex-1 items-end justify-center'
                    >
                        <span
                            className='w-[62%] max-w-6 origin-bottom rounded-t-[4px] bg-moss group-data-[anim=armed]/monitor:scale-y-0 motion-safe:group-data-[anim=play]/monitor:animate-grow-y'
                            style={{
                                height: `${Math.round((entry.value / WEEK_MAX) * 100)}%`,
                                animationDelay: `${400 + index * 90}ms`
                            }}
                        />
                    </div>
                ))}
            </div>

            <div className='mt-1 flex text-[10px] text-muted-foreground'>
                {WEEK.map((entry) => (
                    <span key={entry.day} className='flex-1 text-center'>
                        {entry.day}
                    </span>
                ))}
            </div>
        </Panel>
    )
}

const STEPS = [
    {
        number: '01',
        label: 'Build',
        title: 'Describe the job in plain words',
        body: 'Tell Fieldhand what the work is and which tools it touches. It drafts the agent, its steps and its guardrails for you to review.',
        sky: 'bg-[linear-gradient(160deg,#fbe4d4_0%,#f2dbe6_48%,#d9dbf6_100%)]',
        visual: <BuildVisual />
    },
    {
        number: '02',
        label: 'Run',
        title: 'Let it work across your stack',
        body: 'Agents take real actions in Slack, HubSpot, Notion and the rest of your tools, around the clock, with every step logged.',
        sky: 'bg-[linear-gradient(160deg,#eef3d2_0%,#f3f6e6_45%,#dbe8f1_100%)]',
        visual: <RunVisual />
    },
    {
        number: '03',
        label: 'Monitor',
        title: 'Measure what it delivers',
        body: 'Follow activity, accuracy, cost and time saved for every agent, and catch drift before it turns into a problem.',
        sky: 'bg-[linear-gradient(160deg,#dcdff7_0%,#e6e0f2_50%,#f7ddd0_100%)]',
        visual: <MonitorVisual />
    }
]

export const WorkflowSection: FC = (): ReactNode => {
    return (
        <section
            id='workflow'
            aria-labelledby='workflow-title'
            className='relative isolate overflow-hidden bg-white py-24 sm:py-32'
        >
            <TileField cells={CORNER_TILES} size='64px' className='top-10 -right-6 max-lg:hidden' />

            <Reveal className='mx-auto flex max-w-3xl flex-col items-center px-5 text-center'>
                <SectionBadge>How it works</SectionBadge>

                <h2
                    id='workflow-title'
                    className='mt-6 font-heading text-[clamp(2.1rem,3.1vw,2.8rem)] leading-[1.18] tracking-[-0.03em] text-balance text-ink'
                >
                    Build, run and monitor agents <br className='max-md:hidden' />
                    without writing code.
                </h2>

                <p className='mt-5 max-w-[56ch] text-[17px] leading-relaxed text-muted-foreground'>
                    Describe the work, connect your tools and let the agent run. You stay in control of every step and see exactly what it delivers.
                </p>
            </Reveal>

            <ol className='mx-auto mt-16 grid w-full max-w-[1180px] gap-5 px-5 sm:px-8 lg:grid-cols-3'>
                {STEPS.map((step, index) => (
                    <li key={step.number}>
                        <Reveal delay={index * 120} className='h-full'>
                            <article className='flex h-full flex-col rounded-[34px] border border-border bg-white p-3 shadow-[0_24px_60px_-40px_rgba(20,23,15,0.35)]'>
                                <div className={cn('relative flex min-h-[270px] items-center overflow-hidden rounded-[26px] px-6 py-8 sm:px-8', step.sky)}>
                                    <GlassTiles cells={[[0, 0], [1, 1]]} size='44px' animated={false} className='top-4 right-4 opacity-80' />
                                    <div aria-hidden className='relative w-full'>
                                        {step.visual}
                                    </div>
                                </div>

                                <div className='px-4 pt-7 pb-5 sm:px-5'>
                                    <p className='flex items-center gap-2.5 text-[14px] font-medium text-muted-foreground'>
                                        <span className='rounded-full bg-sage px-2.5 py-0.5 font-mono text-[12px] text-ink/80'>
                                            {step.number}
                                        </span>
                                        {step.label}
                                    </p>

                                    <h3 className='mt-4 font-heading text-[25px] leading-tight tracking-[-0.025em] text-ink'>
                                        {step.title}
                                    </h3>

                                    <p className='mt-3 text-[16px] leading-relaxed text-muted-foreground'>
                                        {step.body}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    </li>
                ))}
            </ol>
        </section>
    )
}
